import { useState } from 'react';
import { Card, Text, Button, Input, Switch, Spinner } from '@fluentui/react-components';
import { useAuth } from '@/auth/AuthContext';
import './Settings.css';

interface Setting {
  id: string;
  category: string;
  label: string;
  value: string | number | boolean;
  type: 'text' | 'boolean' | 'number' | 'email';
  description?: string;
}

const DEMO_SETTINGS: Setting[] = [
  {
    id: 'api-key-rotation',
    category: 'Security',
    label: 'API Key Rotation Period',
    value: 30,
    type: 'number',
    description: 'Days before API keys expire and need rotation',
  },
  {
    id: 'mfa-required',
    category: 'Security',
    label: 'Require MFA for Admins',
    value: true,
    type: 'boolean',
    description: 'Enforce multi-factor authentication for all admin users',
  },
  {
    id: 'session-timeout',
    category: 'Security',
    label: 'Session Timeout (minutes)',
    value: 60,
    type: 'number',
    description: 'Automatically logout inactive sessions',
  },
  {
    id: 'audit-retention',
    category: 'Compliance',
    label: 'Audit Log Retention (days)',
    value: 365,
    type: 'number',
    description: 'Keep audit logs for compliance purposes',
  },
  {
    id: 'data-export',
    category: 'Compliance',
    label: 'Allow Data Export',
    value: true,
    type: 'boolean',
    description: 'Allow tenants to export their data',
  },
  {
    id: 'notifications-email',
    category: 'Notifications',
    label: 'Admin Notification Email',
    value: 'admin@sherpai.com',
    type: 'email',
    description: 'Email address for system notifications',
  },
  {
    id: 'alert-rate-limit',
    category: 'Notifications',
    label: 'Alert on Rate Limit Breach',
    value: true,
    type: 'boolean',
    description: 'Notify admins when tenants exceed rate limits',
  },
  {
    id: 'brand-name',
    category: 'Appearance',
    label: 'Portal Name',
    value: 'Sherpai Management Portal',
    type: 'text',
    description: 'Display name for the portal',
  },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<Setting[]>(DEMO_SETTINGS);
  const [selectedCategory, setSelectedCategory] = useState<string>('Security');
  const [isSaving, setIsSaving] = useState(false);

  const categories = Array.from(new Set(settings.map((s) => s.category)));
  const currentSettings = settings.filter((s) => s.category === selectedCategory);

  const handleUpdateSetting = (id: string, newValue: string | boolean | number) => {
    setSettings(
      settings.map((s) => (s.id === id ? { ...s, value: newValue } : s))
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSaving(false);
    alert('Settings saved successfully!');
  };

  return (
    <div className="settings-container">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <Text size={300}>Configure system settings and preferences</Text>
        </div>
      </div>

      {user?.role !== 'owner' && (
        <Card className="permission-banner">
          <Text>You have read-only access to settings. Contact the owner to make changes.</Text>
        </Card>
      )}

      <div className="settings-layout">
        <div className="settings-sidebar">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-button ${
                selectedCategory === category ? 'active' : ''
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="settings-content">
          {currentSettings.map((setting) => (
            <Card key={setting.id} className="setting-item">
              <div className="setting-header">
                <div>
                  <Text size={300} weight="bold">
                    {setting.label}
                  </Text>
                  {setting.description && (
                    <Text size={200} className="setting-description">
                      {setting.description}
                    </Text>
                  )}
                </div>
              </div>

              <div className="setting-control">
                {setting.type === 'boolean' ? (
                  <Switch
                    checked={Boolean(setting.value)}
                    onChange={(e) =>
                      handleUpdateSetting(setting.id, e.currentTarget.checked)
                    }
                    disabled={user?.role !== 'owner'}
                  />
                ) : (
                  <Input
                    type={setting.type}
                    value={String(setting.value)}
                    onChange={(e) =>
                      handleUpdateSetting(
                        setting.id,
                        setting.type === 'number'
                          ? Number(e.target.value)
                          : e.target.value
                      )
                    }
                    disabled={user?.role !== 'owner'}
                    style={{ maxWidth: '300px' }}
                  />
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {user?.role === 'owner' && (
        <div className="settings-actions">
          <Button
            appearance="primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? <Spinner size="tiny" /> : 'Save Settings'}
          </Button>
          <Button appearance="secondary" onClick={() => setSettings(DEMO_SETTINGS)}>
            Reset to Default
          </Button>
        </div>
      )}
    </div>
  );
}
